<template>
  <div class="prime-payers-catalog">
    <div class="catalog-toolbar">
      <div class="search-input-wrapper">
        <i class="las la-search search-icon"></i>
        <input
          v-model="searchTerm"
          type="text"
          class="search-input"
          placeholder="Search by name…"
          @input="handleSearchInput"
        />
        <button
          v-if="searchTerm"
          type="button"
          class="clear-search-btn"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <i class="las la-times"></i>
        </button>
      </div>
      <span v-if="!loading && !error" class="result-count">
        {{ count }} result{{ count === 1 ? '' : 's' }}
      </span>
    </div>

    <div v-if="loading" class="state-block">
      <BaseSpinner />
      <p>Loading prime payers...</p>
    </div>

    <div v-else-if="error" class="state-block error">
      <p>{{ error }}</p>
      <BaseButton
        stylevariant="primary"
        :cus_style="{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.875rem' }"
        @click="loadPayers"
      >
        Retry
      </BaseButton>
    </div>

    <div v-else-if="payers.length === 0" class="state-block">
      <i class="las la-users empty-icon"></i>
      <p v-if="appliedSearch">No prime payers match “{{ appliedSearch }}”</p>
      <p v-else>No prime payers found</p>
    </div>

    <template v-else>
      <div class="catalog-table-wrapper">
        <table class="catalog-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Parent</th>
              <th>Sector</th>
              <th>Moody’s Rating</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payer in payers" :key="payer.id">
              <td>{{ displayValue(payer.name) }}</td>
              <td>{{ displayValue(payer.parent) }}</td>
              <td>{{ displayValue(payer.sector) }}</td>
              <td>{{ displayValue(payer.moody_rating) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <BasePagination
        v-if="count > 0"
        :totalRows="count"
        :activePage="page"
        :pageSize="pageSize"
        @set-page="setPage"
      />
    </template>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';
import BasePagination from '@/components/UI/BasePagination.vue';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'PrimePayersCatalog',
  components: { BaseButton, BaseSpinner, BasePagination },
  data() {
    return {
      loading: false,
      error: null,
      payers: [],
      count: 0,
      next: null,
      previous: null,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      searchTerm: '',
      appliedSearch: '',
      debounceTimer: null,
      hasLoaded: false
    };
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    displayValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      return value;
    },

    ensureLoaded() {
      if (!this.hasLoaded) {
        this.loadPayers();
      }
    },

    handleSearchInput() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.page = 1;
        this.loadPayers();
      }, SEARCH_DEBOUNCE_MS);
    },

    clearSearch() {
      this.searchTerm = '';
      this.page = 1;
      this.loadPayers();
    },

    setPage(page, pageSize) {
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      if (page >= 1) {
        this.page = page;
        this.loadPayers();
      }
    },

    async loadPayers() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize
        };

        const search = (this.searchTerm || '').trim();
        if (search) {
          params.search = search;
        }

        const response = await ChatbotService.getPrimePayers(params);
        const rows = Array.isArray(response?.data) ? response.data : [];

        this.payers = rows;
        this.count = typeof response?.count === 'number' ? response.count : rows.length;
        this.next = response?.next ?? null;
        this.previous = response?.previous ?? null;
        this.appliedSearch = search;
        this.hasLoaded = true;
      } catch (err) {
        console.error('Error loading prime payers:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to load prime payers.';
        this.payers = [];
        this.count = 0;
        this.next = null;
        this.previous = null;
        this.hasLoaded = true;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../index.scss';

.prime-payers-catalog {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.catalog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 480px;
}

.search-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 1rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.6rem 2.25rem 0.6rem 2.35rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: $brand-font-regular;
  color: #2d3748;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.15);
  }

  &::placeholder {
    color: #a0aec0;
  }
}

.clear-search-btn {
  position: absolute;
  right: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #a0aec0;
  cursor: pointer;
  padding: 0.2rem;
  line-height: 1;
  font-size: 1rem;

  &:hover {
    color: #718096;
  }
}

.result-count {
  font-size: 0.85rem;
  color: #718096;
  font-family: $brand-font-regular;
  white-space: nowrap;
}

.state-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: #718096;
  font-family: $brand-font-regular;
  text-align: center;

  p {
    margin: 0;
  }

  &.error {
    color: #c53030;
  }
}

.empty-icon {
  font-size: 2.5rem;
  color: #cbd5e0;
}

.catalog-table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e6e9f0;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
}

.catalog-table {
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: #f0f4fa;
  }

  th {
    padding: 0.75rem 1.25rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 700;
    color: #2d3748;
    font-family: $brand-font-medium;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid #edf2f7;
    transition: background 0.12s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8fafc;
    }
  }

  td {
    padding: 0.75rem 1.25rem;
    text-align: left;
    vertical-align: middle;
    font-size: 0.9rem;
    color: #2d3748;
    font-family: $brand-font-regular;
  }

}
</style>
